import { MNSproCloudApi } from '../..';
import {
  AllowedActionsResponse,
  AppConsent,
  MemberUpdateChanges,
  ModulePermissions,
  PermissionConsent,
  Person,
  RolePermissions,
  RolePermissionsCreateEntry,
  RolePermissionsUpdateEntry,
} from '../../api/v2';

export class PermissionsService {
  static async getPermissionsRoles(
    authToken: string,
    organizationId: string,
  ): Promise<RolePermissions[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.getPermissionsRoles(organizationId);

    return result.data;
  }

  static async getPermissionsRole(
    authToken: string,
    organizationId: string,
    id: string,
  ): Promise<RolePermissions> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.getPermissionsRole(id, organizationId);

    return result.data;
  }

  static async getAllPermissionsModules(
    authToken: string,
    organizationId: string,
  ): Promise<ModulePermissions[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.getPermissionsModulesAll(organizationId);
    return result.data;
  }

  static async getPermissionsModule(
    authToken: string,
    organizationId: string,
    name: string,
  ): Promise<ModulePermissions> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.getPermissionsModules(organizationId, name);
    return result.data;
  }

  static async getPermissionsRoleMembers(
    authToken: string,
    organizationId: string,
    id: string,
  ): Promise<Person[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.getPermissionsRoleMembers(organizationId, id);
    return result.data;
  }

  static async createPermissionsRole(
    authToken: string,
    organizationId: string,
    rolePermissionsCreateEntry: RolePermissionsCreateEntry,
  ): Promise<RolePermissions> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.createPermissionsRole(
      organizationId,
      rolePermissionsCreateEntry,
    );
    return result.data;
  }

  static async deletePermissionsRole(
    authToken: string,
    organizationId: string,
    id: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.deletePermissionsRole(organizationId, id);
    return result.data;
  }

  static async updatePermissionsRole(
    authToken: string,
    organizationId: string,
    rolePermissionsUpdateEntry: RolePermissionsUpdateEntry,
  ): Promise<RolePermissions> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.updatePermissionsRole(
      organizationId,
      rolePermissionsUpdateEntry,
    );
    return result.data;
  }

  static async updatePermissionsRoleMembers(
    authToken: string,
    organizationId: string,
    id: string,
    memberUpdateChanges: MemberUpdateChanges,
  ): Promise<Person[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const result = await api.updatePermissionsRoleMembers(
      id,
      organizationId,
      memberUpdateChanges,
    );
    return result.data;
  }
  static async addMembersToRole(
    authToken: string,
    organizationId: string,
    roleId: string,
    memberUpdateChanges: MemberUpdateChanges,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);
    await api.updatePermissionsRoleMembers(
      roleId,
      organizationId,
      memberUpdateChanges,
    );
  }
  static async removeMembersFromRole(
    authToken: string,
    organizationId: string,
    roleId: string,
    memberUpdateChanges: MemberUpdateChanges,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);
    await api.updatePermissionsRoleMembers(
      roleId,
      organizationId,
      memberUpdateChanges,
    );
  }

  static async getCustomRoleDescription(
    authToken: string,
    organizationId: string,
    permissionId: string,
  ): Promise<AllowedActionsResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);
    const result = await api.getAllowedActions(organizationId, permissionId);

    return result.data;
  }

  static async getSyncRequiredPermissionIds(
    authToken: string,
    organizationId: string,
  ): Promise<string[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);
    const result = await api.getAllowedActionsSyncState(organizationId);

    return result.data;
  }

  static async syncRequiredPermissions(
    authToken: string,
    organizationId: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    await api.syncAllowedActions(organizationId);
  }

  static async validateAppConsent(
    authToken: string,
    organizationId: string,
    appId?: string,
    signal?: AbortSignal,
  ): Promise<AppConsent> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const response = await api.getConsent(organizationId, appId, { signal });

    return response.data;
  }

  static async validatePermissionConsent(
    authToken: string,
    organizationId: string,
    permissionId: string,
    appId?: string,
    signal?: AbortSignal,
  ): Promise<PermissionConsent> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const response = await api.getConsentOfPermission(
      organizationId,
      permissionId,
      appId,
      { signal },
    );

    return response.data;
  }
}
